import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  adminArchiveUser,
  adminCreateUser,
  adminListUsers,
  adminResendInvite,
  adminUnarchiveUser,
} from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { AdminListUsersRequestSchema } from '../../gen/cortado/v1/admin_pb'
import { create } from '@bufbuild/protobuf'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../components/ui/sortable-header'
import { Badge } from '../../components/ui/badge'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../../components/ui/popover'
import { Archive, ArchiveRestore, Info, Loader2, MailPlus, UserPlus } from 'lucide-react'

type UserSortField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'organization'
  | 'lastLogin'
  | 'created'
  | 'status'

const formatDateTime = (seconds?: bigint) => {
  if (!seconds) return '-'
  return new Date(Number(seconds) * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

const formatDate = (seconds?: bigint) => {
  if (!seconds) return '-'
  return new Date(Number(seconds) * 1000).toLocaleDateString(undefined, {
    dateStyle: 'medium',
  })
}

function ActionHintPopover({
  children,
  title,
  description,
}: {
  children: ReactNode
  title: string
  description: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <span
          className="inline-flex"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          {children}
        </span>
      </PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="center"
        className="w-56 p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="space-y-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-muted-foreground text-xs leading-snug">{description}</p>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function ActionsKeyPopover() {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Show actions key"
          className="text-muted-foreground hover:text-foreground -my-1 size-7"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onClick={() => setOpen(true)}
        >
          <Info className="h-4 w-4" />
        </Button>
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="end"
        className="w-64 p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="space-y-3">
          <p className="text-sm font-medium">Actions key</p>
          <div className="space-y-2 text-xs">
            <div className="flex items-start gap-2">
              <MailPlus className="text-muted-foreground mt-0.5 h-4 w-4" />
              <div>
                <p className="font-medium">Resend invite</p>
                <p className="text-muted-foreground">Send a fresh invitation email.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Archive className="text-muted-foreground mt-0.5 h-4 w-4" />
              <div>
                <p className="font-medium">Archive user</p>
                <p className="text-muted-foreground">Make the account inactive.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ArchiveRestore className="text-muted-foreground mt-0.5 h-4 w-4" />
              <div>
                <p className="font-medium">Unarchive user</p>
                <p className="text-muted-foreground">Reactivate an archived account.</p>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export const Route = createFileRoute('/_admin/users')({
  component: AdminDashboard,
})

function AdminDashboard() {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [archiveDialogUser, setArchiveDialogUser] = useState<{
    id: string
    email: string
  } | null>(null)
  const [unarchiveDialogUser, setUnarchiveDialogUser] = useState<{
    id: string
    email: string
  } | null>(null)
  const [creating, setCreating] = useState(false)
  const { sortField, sortDirection, toggleSort } = useTableSort<UserSortField>('firstName')

  const { data: usersData, isLoading } = useQuery(
    adminListUsers,
    create(AdminListUsersRequestSchema, { pageSize: 100 }),
  )

  const sortedUsers = useMemo(() => {
    const users = usersData?.users ?? []
    const dir = sortDirection === 'asc' ? 1 : -1
    const getOrgNames = (userId: string) =>
      usersData?.userOrganizations?.[userId]?.organizations
        ?.map((org) => org.name)
        .join(', ') ?? ''

    return [...users].sort((a, b) => {
      switch (sortField) {
        case 'firstName':
          return compareStrings(a.firstName, b.firstName) * dir
        case 'lastName':
          return compareStrings(a.lastName, b.lastName) * dir
        case 'email':
          return compareStrings(a.email, b.email) * dir
        case 'organization':
          return compareStrings(getOrgNames(a.id), getOrgNames(b.id)) * dir
        case 'lastLogin':
          return compareNumbers(
            a.lastLoginAt?.seconds ?? 0n,
            b.lastLoginAt?.seconds ?? 0n,
          ) * dir
        case 'created':
          return compareNumbers(
            a.createdAt?.seconds ?? 0n,
            b.createdAt?.seconds ?? 0n,
          ) * dir
        case 'status':
          return compareStrings(
            a.archivedAt ? 'Archived' : 'Active',
            b.archivedAt ? 'Archived' : 'Active',
          ) * dir
        default:
          return 0
      }
    })
  }, [usersData, sortField, sortDirection])

  const createUserMutation = useMutation(adminCreateUser, {
    onSuccess: (_data, variables) => {
      toast.success(`User ${variables.email} created successfully`)
      setDialogOpen(false)
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: adminListUsers,
          cardinality: undefined,
        }),
      })
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create user')
    },
    onSettled: () => {
      setCreating(false)
    },
  })

  const resendMutation = useMutation(adminResendInvite, {
    onSuccess: () => {
      toast.success('Invite resent successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to resend invite: ${error.message}`)
    },
  })

  const archiveMutation = useMutation(adminArchiveUser, {
    onSuccess: () => {
      toast.success('User archived')
      setArchiveDialogUser(null)
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: adminListUsers,
          cardinality: undefined,
        }),
      })
    },
    onError: (error: Error) => {
      toast.error(`Failed to archive user: ${error.message}`)
    },
  })

  const unarchiveMutation = useMutation(adminUnarchiveUser, {
    onSuccess: () => {
      toast.success('User unarchived')
      setUnarchiveDialogUser(null)
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: adminListUsers,
          cardinality: undefined,
        }),
      })
    },
    onError: (error: Error) => {
      toast.error(`Failed to unarchive user: ${error.message}`)
    },
  })

  const handleCreateUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCreating(true)

    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const firstName = (formData.get('firstName') as string) || undefined
    const lastName = (formData.get('lastName') as string) || undefined

    createUserMutation.mutate({ email, firstName, lastName })
    ;(e.target as HTMLFormElement).reset()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-muted-foreground text-sm">
            {usersData?.totalCount !== undefined
              ? `${usersData.totalCount} total users`
              : 'Manage platform users'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto">
              <UserPlus data-icon="inline-start" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="create-email">Email *</Label>
                <Input
                  id="create-email"
                  name="email"
                  type="email"
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="create-firstName">First Name</Label>
                  <Input
                    id="create-firstName"
                    name="firstName"
                    type="text"
                    placeholder="John"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="create-lastName">Last Name</Label>
                  <Input
                    id="create-lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader
                  active={sortField === 'email'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('email')}
                >
                  Email
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'firstName'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('firstName')}
                >
                  First Name
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'lastName'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('lastName')}
                >
                  Last Name
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'created'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('created')}
                >
                  Created
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'organization'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('organization')}
                >
                  Organization
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'lastLogin'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('lastLogin')}
                >
                  Last Login
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'status'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('status')}
                >
                  Status
                </SortableHeader>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Actions
                  <ActionsKeyPopover />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  Loading users...
                </TableCell>
              </TableRow>
            ) : !sortedUsers.length ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              sortedUsers.map((user) => {
                const orgs =
                  usersData?.userOrganizations?.[user.id]?.organizations ?? []
                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.firstName || '-'}</TableCell>
                    <TableCell>{user.lastName || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(user.createdAt?.seconds)}
                    </TableCell>
                    <TableCell>
                      {orgs.length === 0 ? (
                        <span className="text-muted-foreground">-</span>
                      ) : (
                        orgs.map((org, i) => (
                          <span key={org.id}>
                            <Link
                              to="/organizations/$organizationId"
                              params={{ organizationId: org.id }}
                              className="text-primary hover:underline"
                            >
                              {org.name}
                            </Link>
                            {i < orgs.length - 1 ? ', ' : ''}
                          </span>
                        ))
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt.seconds) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {user.archivedAt ? (
                        <Badge variant="danger">Archived</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!user.firstName && !user.archivedAt && (
                          <ActionHintPopover
                            title="Resend invite"
                            description="Send this user a fresh magic-link invitation email."
                          >
                            <Button
                              variant="outline"
                              size="icon-sm"
                              aria-label={`Resend invite to ${user.email}`}
                              disabled={resendMutation.isPending}
                              onClick={() => resendMutation.mutate({ email: user.email })}
                            >
                              <MailPlus className="h-4 w-4" />
                            </Button>
                          </ActionHintPopover>
                        )}
                        {user.archivedAt ? (
                          <ActionHintPopover
                            title="Unarchive user"
                            description="Reactivate this account so the user can sign in again."
                          >
                            <Button
                              variant="outline"
                              size="icon-sm"
                              aria-label={`Unarchive ${user.email}`}
                              disabled={unarchiveMutation.isPending}
                              onClick={() =>
                                setUnarchiveDialogUser({ id: user.id, email: user.email })
                              }
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </Button>
                          </ActionHintPopover>
                        ) : (
                          <ActionHintPopover
                            title="Archive user"
                            description="Open a confirmation dialog and make this account inactive."
                          >
                            <Button
                              variant="outline"
                              size="icon-sm"
                              aria-label={`Archive ${user.email}`}
                              disabled={archiveMutation.isPending}
                              onClick={() =>
                                setArchiveDialogUser({ id: user.id, email: user.email })
                              }
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </ActionHintPopover>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={!!archiveDialogUser}
        onOpenChange={(open) => !open && setArchiveDialogUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive user?</DialogTitle>
            <DialogDescription>
              This will make {archiveDialogUser?.email} inactive. They will not be
              able to access Cortado after signing in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setArchiveDialogUser(null)}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!archiveDialogUser || archiveMutation.isPending}
              onClick={() => {
                if (!archiveDialogUser) return
                archiveMutation.mutate({ userId: archiveDialogUser.id })
              }}
            >
              {archiveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Archive User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!unarchiveDialogUser}
        onOpenChange={(open) => !open && setUnarchiveDialogUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unarchive user?</DialogTitle>
            <DialogDescription>
              This will reactivate {unarchiveDialogUser?.email}. They will be able
              to sign in to Cortado again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUnarchiveDialogUser(null)}
              disabled={unarchiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!unarchiveDialogUser || unarchiveMutation.isPending}
              onClick={() => {
                if (!unarchiveDialogUser) return
                unarchiveMutation.mutate({ userId: unarchiveDialogUser.id })
              }}
            >
              {unarchiveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Unarchive User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
